/**
 * KpiStrip — four cards on the invoices list header.
 *
 * Cards (per `prototype/sales/invoices.html` + `10-sales-invoicing-spec.md`):
 *   1. Outstanding · X open invoices · Y customers + WoW Δ chip (PR-F addendum)
 *   2. Overdue · oldest N days
 *   3. Paid · this month · K invoices + month-over-month Δ chip
 *   4. Drafts · saved drafts ready to send
 *
 * Lakh/crore-formatted values for compactness. Counts come from the
 * list-page query's `kpis` + `counts` blocks.
 *
 * Honesty note (PR-A): "Outstanding" is now NET of receipts (PR-C) and
 * credit notes (PR-G). The Info tooltip explains the math source.
 *
 * WoW deltas (PR-F addendum): the Outstanding tile shows a signed Δ
 * computed flow-style (issued₇ − received₇ − cn₇). The Paid-this-month
 * tile compares against last calendar month's receipts. Stock metrics
 * Overdue/Drafts don't show Δ — they need real T−7d snapshots which
 * land with the snapshot vertical (V1.x).
 */
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Check,
  Clock3,
  FileText,
  Info,
  Pencil,
  type LucideIcon,
} from "lucide-react";
import { Card, CardBody } from "@flint/ui";
import { formatLakhCrore } from "../lib/format-money.ts";

/** Single source of truth for the "Outstanding" tooltip copy — also reused on
 *  the Workspace Home Receivables card so both surfaces explain the same way. */
export const OUTSTANDING_TOOLTIP =
  'Total of issued + partially-paid + overdue invoices, NET of receipts received and credit notes raised. The honest "still owed to you" figure.';

export interface KpiStripProps {
  outstandingPaise: string;
  outstandingCustomerCount: number;
  outstandingInvoiceCount: number;
  overduePaise: string;
  overdueInvoiceCount: number;
  oldestOverdueDays: number | null;
  paidThisMonthPaise: string;
  paidThisMonthInvoiceCount: number;
  draftCount: number;
  /** Signed paise as string. Positive = stock grew. Optional — older callers may not pass. */
  outstanding7dDeltaPaise?: string;
  /** Cash received last calendar month (paise). Pairs with paidThisMonthPaise for the Δ. */
  paidLastMonthPaise?: string;
}

export function KpiStrip(props: KpiStripProps) {
  // Outstanding Δ — direction matters: rising stock is bad (more owed),
  // falling stock is good (you collected). Pill colour follows the sign.
  const outstandingDelta = parseSignedPaise(props.outstanding7dDeltaPaise);
  // Paid-this-month vs last month — direction matters opposite: rising
  // is good (more cash), falling is bad. Same pill, inverted tone.
  const paidDelta =
    props.paidLastMonthPaise !== undefined
      ? parseSignedPaise(props.paidThisMonthPaise) - parseSignedPaise(props.paidLastMonthPaise)
      : null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      <Tile
        accent="sky"
        icon={FileText}
        label="Outstanding"
        labelHint={OUTSTANDING_TOOLTIP}
        value={formatLakhCrore(props.outstandingPaise)}
        meta={`${props.outstandingInvoiceCount} open · ${props.outstandingCustomerCount} customers`}
        delta={
          outstandingDelta !== null
            ? {
                signedPaise: outstandingDelta,
                // For Outstanding, growing is BAD. Tone flips.
                tone: outstandingDelta > 0n ? "bad" : outstandingDelta < 0n ? "good" : "flat",
                suffix: "vs 7d ago",
              }
            : undefined
        }
      />
      <Tile
        accent="rose"
        icon={Clock3}
        label="Overdue"
        value={formatLakhCrore(props.overduePaise)}
        meta={
          props.overdueInvoiceCount === 0
            ? "No overdue invoices"
            : props.oldestOverdueDays !== null
              ? `${props.overdueInvoiceCount} invoices · oldest ${props.oldestOverdueDays}d`
              : `${props.overdueInvoiceCount} invoices`
        }
        // No delta — overdue is a stock metric; flow-style approximation
        // wouldn't be meaningful. T-7d snapshot vertical lights this up.
      />
      <Tile
        accent="emerald"
        icon={Check}
        label="Paid · this month"
        value={formatLakhCrore(props.paidThisMonthPaise)}
        meta={`${props.paidThisMonthInvoiceCount} invoice${
          props.paidThisMonthInvoiceCount === 1 ? "" : "s"
        }`}
        delta={
          paidDelta !== null
            ? {
                signedPaise: paidDelta,
                // For Paid, growing is GOOD.
                tone: paidDelta > 0n ? "good" : paidDelta < 0n ? "bad" : "flat",
                suffix: "vs last month",
              }
            : undefined
        }
      />
      <Tile
        accent="muted"
        icon={Pencil}
        label="Drafts"
        value={`${props.draftCount}`}
        meta={
          props.draftCount === 0
            ? "No drafts saved"
            : `${props.draftCount} saved draft${props.draftCount === 1 ? "" : "s"}`
        }
      />
    </div>
  );
}

// ── Tile + delta pill ────────────────────────────────────────────

type DeltaTone = "good" | "bad" | "flat";

interface DeltaProps {
  signedPaise: bigint;
  tone: DeltaTone;
  suffix: string;
}

interface TileProps {
  accent: "sky" | "rose" | "emerald" | "muted";
  icon: LucideIcon;
  label: string;
  /** Optional tooltip shown via a small Info icon next to the label. */
  labelHint?: string | undefined;
  value: string;
  meta: string;
  delta?: DeltaProps | undefined;
}

const ACCENT_BAR: Record<TileProps["accent"], string> = {
  sky: "bg-sky-500",
  rose: "bg-rose-500",
  emerald: "bg-emerald-500",
  muted: "bg-line",
};

const ACCENT_ICON: Record<TileProps["accent"], string> = {
  sky: "bg-sky-50 text-sky-600",
  rose: "bg-rose-50 text-rose-600",
  emerald: "bg-emerald-50 text-emerald-600",
  muted: "bg-bg text-muted",
};

const DELTA_TONE: Record<DeltaTone, string> = {
  good: "bg-emerald-50 text-emerald-700 border-emerald-200",
  bad: "bg-rose-50 text-rose-700 border-rose-200",
  flat: "bg-raised text-muted border-line",
};

function Tile({ accent, icon: Icon, label, labelHint, value, meta, delta }: TileProps) {
  return (
    <Card className="relative min-w-0 overflow-hidden transition-all hover:-translate-y-px hover:border-line-2 hover:shadow-md">
      <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${ACCENT_BAR[accent]}`} />
      <CardBody className="px-4 py-3.5">
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted">
            <p className="min-w-0 truncate flex items-center gap-1">
              {label}
              {labelHint ? (
                <span
                  role="img"
                  className="inline-flex items-center text-muted hover:text-ink-2 cursor-help"
                  title={labelHint}
                  aria-label={labelHint}
                >
                  <Info size={12} aria-hidden />
                </span>
              ) : null}
            </p>
            <span
              aria-hidden
              className={`grid size-7 shrink-0 place-items-center rounded-md ${ACCENT_ICON[accent]}`}
            >
              <Icon size={14} strokeWidth={2} />
            </span>
          </div>
          <p className="text-xl font-bold leading-tight text-ink truncate">{value}</p>
          <div className="mt-1 flex items-center gap-1.5">
            <p className="text-xs text-muted truncate">{meta}</p>
            {delta ? <DeltaPill {...delta} /> : null}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function DeltaPill({ signedPaise, tone, suffix }: DeltaProps) {
  const Icon = signedPaise > 0n ? ArrowUp : signedPaise < 0n ? ArrowDown : ArrowRight;
  const abs = signedPaise < 0n ? -signedPaise : signedPaise;
  // Hide the pill entirely when the delta is exactly zero — no signal.
  if (abs === 0n) return null;
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium border ${DELTA_TONE[tone]} shrink-0`}
      title={`${signedPaise > 0n ? "+" : "-"}${formatLakhCrore(abs.toString())} ${suffix}`}
    >
      <Icon size={10} aria-hidden />
      {formatLakhCrore(abs.toString())}
    </span>
  );
}

/** Parse a signed paise string. Returns null when undefined; throws on garbage. */
function parseSignedPaise(value: string | undefined): bigint {
  if (value === undefined || value === "") return 0n;
  return BigInt(value);
}
